import { createApiClient } from './axios-instance';
import type { QAResponse } from '../types/kms.types';

// Type definitions - Updated for Google FileSearch backend
export interface Space {
  id: string;
  workspace_id: string;
  account_id: string;
  name: string;
  description?: string;
  file_search_store_name?: string; // Google FileSearch store resource name
  created_at: string;
  updated_at: string;
}

export interface SpaceCreate {
  name: string;
  description?: string;
}

export interface SpaceUpdate {
  name?: string;
  description?: string;
}

export interface Document {
  id: string;
  space_id: string;
  account_id: string;
  name: string;
  file_url?: string;
  file_size?: number;
  file_type?: string;
  upload_status: 'pending' | 'processing' | 'ready' | 'failed';
  processing_status?: 'pending' | 'processing' | 'ready' | 'failed';
  error_message?: string;
  created_at: string;
  updated_at: string;
  chunk_count?: number;
  vector_count?: number;

  // Source tracking fields
  external_source?: string | null;
  external_source_id?: string | null;
  external_source_url?: string | null;
  connection_name?: string | null;
}

export interface DocumentUploadResponse {
  documents: Document[];
  processing_tasks: string[];
}

export interface SpaceListResponse {
  success: boolean;
  message: string;
  data: {
    items: Space[];
    total: number;
  };
  meta?: {
    count: number;
    total: number;
    skip: number;
    limit: number;
  };
}

export interface DocumentListResponse {
  data: Document[];
  meta: {
    pagination: {
      total: number;
      page: number;
      size: number;
      pages: number;
    };
  };
}

// Integration types (KMS-INTG-F03)
export interface SlackIntegration {
  id: string;
  workspace_id: string;
  team_id: string;
  team_name: string;
  bot_user_id: string;
  admin_user_id: string;
  admin_email?: string;
  scopes: string;
  is_active: boolean;
  connected_at: string;
  created_at: string;
  updated_at: string;
}

export interface IntegrationListResponse {
  slack?: SlackIntegration[];
}

export interface SlackOAuthUrlResponse {
  oauth_url: string;
  state: string;
}

export interface IntegrationTestResult {
  success: boolean;
  message: string;
  details?: Record<string, unknown>;
}

// Widget types
export interface WidgetSpace {
  id: string;
  name: string;
  description: string | null;
  priority: number;
}

export interface Widget {
  id: string;
  workspace_id: string;
  account_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  spaces: WidgetSpace[];

  // Appearance - Colors
  theme_color: string;
  accent_color: string;
  text_color: string;
  button_color: string;
  button_icon_color: string;

  // Appearance - Button
  button_icon: string;
  button_text: string;
  button_border_radius: string;

  // Layout
  position: string;
  widget_size: string;
  widget_height: string;
  popup_border_radius: string;

  // Branding
  header_title: string;
  bot_name: string;
  avatar_icon: string;
  show_powered_by: boolean;

  // Messaging
  welcome_message: string;
  input_placeholder: string;
  send_button_text: string;
  suggested_questions: string[] | null;

  // Behavior
  auto_open: boolean;
  auto_open_delay: number;
  persist_conversation: boolean;

  // Advanced
  max_message_length: number;
  timeout: number;

  // Security
  allowed_domains: string[] | null;
  rate_limit_per_hour: number;
  rate_limit_per_minute: number;

  created_at: string;
  updated_at: string;
}

export interface WidgetFormData {
  name: string;
  description: string;
  space_ids: string[];
  theme_color: string;
  accent_color: string;
  text_color: string;
  button_color: string;
  button_icon_color: string;
  button_icon: string;
  button_text: string;
  button_border_radius: string;
  position: string;
  widget_size: string;
  widget_height: string;
  popup_border_radius: string;
  header_title: string;
  bot_name: string;
  avatar_icon: string;
  show_powered_by: boolean;
  welcome_message: string;
  input_placeholder: string;
  send_button_text: string;
  suggested_questions: string[];
  auto_open: boolean;
  auto_open_delay: number;
  persist_conversation: boolean;
  max_message_length: number;
  timeout: number;
  allowed_domains: string[];
  rate_limit_per_hour: number;
  rate_limit_per_minute: number;
}

export interface WidgetListResponse {
  items: Widget[];
  total: number;
}

export interface EmbedCodeResponse {
  widget_id: string;
  widget_name: string;
  embed_code: string;
  script_tag: string;
  config_only: string;
}

export const kmsService = {
  // ========================================
  // Space Methods
  // ========================================

  /**
   * List all spaces for workspace with pagination
   */
  async listSpaces(
    idToken: string,
    params: {
      skip?: number;
      limit?: number;
    } = {}
  ): Promise<SpaceListResponse> {
    const { skip = 0, limit = 50 } = params;
    const client = createApiClient(idToken, 'kms');

    const response = await client.get('/kms/spaces', {
      params: {
        skip,
        limit,
      },
    });

    return response.data;
  },

  /**
   * Get a specific space
   */
  async getSpace(idToken: string, spaceId: string): Promise<{ success: boolean; data: Space }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.get(`/kms/spaces/${spaceId}`);
    return response.data;
  },

  /**
   * Create a new space
   */
  async createSpace(
    idToken: string,
    data: SpaceCreate
  ): Promise<{ success: boolean; data: Space }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.post('/kms/spaces', data);
    return response.data;
  },

  /**
   * Update a space
   */
  async updateSpace(
    idToken: string,
    spaceId: string,
    data: SpaceUpdate
  ): Promise<{ success: boolean; data: Space }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.put(`/kms/spaces/${spaceId}`, data);
    return response.data;
  },

  /**
   * Delete a space
   */
  async deleteSpace(idToken: string, spaceId: string): Promise<void> {
    const client = createApiClient(idToken, 'kms');
    await client.delete(`/kms/spaces/${spaceId}`);
  },

  // ========================================
  // Document Methods
  // ========================================

  /**
   * Create document from pasted text content
   */
  async pasteTextContent(
    idToken: string,
    spaceId: string,
    content: string,
    title?: string
  ): Promise<{ data: DocumentUploadResponse }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.post(`/kms/spaces/${spaceId}/documents/paste`, {
      content,
      title,
    });
    return response.data;
  },

  /**
   * Upload a single document to a space
   * Note: Backend currently supports single file upload only
   */
  async uploadDocuments(
    idToken: string,
    spaceId: string,
    files: File[]
  ): Promise<{ data: DocumentUploadResponse }> {
    const client = createApiClient(idToken, 'kms');

    // Upload files one by one (backend accepts single file per request)
    const uploadedDocuments: Document[] = [];
    const processingTasks: string[] = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file); // Changed from 'files' to 'file'

      const response = await client.post(`/kms/spaces/${spaceId}/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Backend returns single document response
      if (response.data?.data?.document) {
        uploadedDocuments.push(response.data.data.document);
        processingTasks.push(response.data.data.processing_task_id);
      }
    }

    return {
      data: {
        documents: uploadedDocuments,
        processing_tasks: processingTasks,
      },
    };
  },

  /**
   * List documents in a space
   */
  async listDocuments(
    idToken: string,
    spaceId: string,
    params: { page?: number; size?: number; sourceFilter?: string } = {}
  ): Promise<DocumentListResponse> {
    const { page = 1, size = 20, sourceFilter } = params;
    const client = createApiClient(idToken, 'kms');

    const response = await client.get(`/kms/spaces/${spaceId}/documents`, {
      params: {
        page,
        size,
        ...(sourceFilter && { source_filter: sourceFilter }),
      },
    });

    return response.data;
  },

  /**
   * Delete a document
   */
  async deleteDocument(idToken: string, spaceId: string, documentId: string): Promise<void> {
    const client = createApiClient(idToken, 'kms');
    await client.delete(`/kms/spaces/${spaceId}/documents/${documentId}`);
  },

  // ========================================
  // Q&A / Chat Methods (Stateless)
  // ========================================

  /**
   * Ask a question and get AI-generated answer with citations (stateless - no session tracking)
   */
  async askQuestion(idToken: string, spaceId: string, question: string): Promise<QAResponse> {
    const client = createApiClient(idToken, 'kms');

    const requestData = {
      question,
    };

    const response = await client.post<QAResponse>(`/kms/spaces/${spaceId}/qa/ask`, requestData, {
      timeout: 90000, // 90 seconds for LLM processing
    });

    return response.data;
  },

  /**
   * Get suggested questions for a space
   */
  async getSuggestedQuestions(
    idToken: string,
    spaceId: string,
    limit: number = 5
  ): Promise<{ data: string[] }> {
    const client = createApiClient(idToken, 'kms');

    const response = await client.get(`/kms/spaces/${spaceId}/qa/suggestions`, {
      params: { limit },
    });

    return response.data;
  },

  // ========================================
  // Integration Methods (KMS-INTG-F03)
  // ========================================

  /**
   * Get workspace-level Slack integration
   */
  async getWorkspaceSlackIntegration(idToken: string): Promise<{ data: IntegrationListResponse }> {
    const client = createApiClient(idToken, 'portal');
    const response = await client.get(`/slack/integrations`);
    return response.data;
  },

  /**
   * Get a specific Slack integration by ID
   */
  async getWorkspaceSlackIntegrationById(
    idToken: string,
    integrationId: string
  ): Promise<{ data: SlackIntegration }> {
    const client = createApiClient(idToken, 'portal');
    const response = await client.get(`/slack/integrations/${integrationId}`);
    return response.data;
  },

  /**
   * Get Slack OAuth URL for workspace-level connection (all spaces)
   */
  async getWorkspaceSlackOAuthUrl(
    idToken: string,
    workspaceId: string
  ): Promise<{ data: SlackOAuthUrlResponse }> {
    const client = createApiClient(idToken, 'portal');
    const response = await client.post(`/slack/integrations/oauth-url`, {
      workspace_id: workspaceId,
    });
    return response.data;
  },

  /**
   * Disconnect a Slack integration
   */
  async disconnectSlackIntegrationWorkspace(
    idToken: string,
    integrationId: string
  ): Promise<{ success: boolean; message: string }> {
    const client = createApiClient(idToken, 'portal');
    const response = await client.delete(`/slack/integrations/${integrationId}`);
    return response.data;
  },

  /**
   * List all Slack users for an integration
   */
  async listSlackUsers(
    idToken: string,
    integrationId: string
  ): Promise<{
    data: {
      users: Array<{
        id: number;
        slack_user_id: string;
        integration_id: string;
        email: string;
        display_name: string | null;
        avatar_url: string | null;
        accessible_spaces: string[];
        created_at: string;
        updated_at: string;
      }>;
      total: number;
      without_access: number;
    };
  }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.get(`/kms/slack/integrations/${integrationId}/users`);
    return response.data;
  },

  /**
   * Grant Slack user access to spaces
   */
  async grantSlackUserAccess(
    idToken: string,
    userId: number,
    spaceIds: string[]
  ): Promise<{ data: any; message: string }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.post(`/kms/slack/users/${userId}/grant-access`, {
      space_ids: spaceIds,
    });
    return response.data;
  },

  /**
   * Revoke Slack user access to a space
   */
  async revokeSlackUserAccess(
    idToken: string,
    userId: number,
    spaceId: string
  ): Promise<{ data: any; message: string }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.delete(`/kms/slack/users/${userId}/spaces/${spaceId}`);
    return response.data;
  },

  /**
   * Get access matrix for Slack users and spaces
   */
  async getAccessMatrix(
    idToken: string,
    integrationId: string,
    page: number = 1,
    limit: number = 5
  ): Promise<{
    data: {
      users: Array<{ id: number; display_name: string | null; email: string }>;
      spaces: Array<{ id: string; name: string }>;
      access_matrix: Record<string, boolean>;
      pagination: {
        page: number;
        limit: number;
        total_users: number;
        total_pages: number;
      };
    };
  }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.get(`/kms/slack/integrations/${integrationId}/access-matrix`, {
      params: { page, limit },
    });
    return response.data;
  },

  /**
   * Bulk update Slack user access to spaces
   */
  async bulkUpdateAccess(
    idToken: string,
    integrationId: string,
    changes: Array<{ user_id: number; space_id: string; grant: boolean }>
  ): Promise<{
    data: { granted: number; revoked: number; total_changes: number };
    message: string;
  }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.post(
      `/kms/slack/integrations/${integrationId}/access-matrix/bulk-update`,
      { changes }
    );
    return response.data;
  },

  // ========================================
  // CLOUD STORAGE INTEGRATION
  // ========================================

  /**
   * Get Cloud Storage OAuth authorization URL
   */
  async getDriveOAuthUrl(
    idToken: string,
    spaceId: string,
    redirectUri: string
  ): Promise<{ data: { oauth_url: string; state: string } }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.post('/kms/storage/oauth/authorize', null, {
      params: { space_id: spaceId, redirect_uri: redirectUri },
    });
    return response.data;
  },

  /**
   * Handle Cloud Storage OAuth callback and get connector_id + access_token
   */
  async handleDriveOAuthCallback(
    idToken: string,
    code: string,
    state: string
  ): Promise<{
    data: { success: boolean; message: string; connector_id: string; access_token: string };
  }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.get('/kms/storage/oauth/callback', {
      params: { code, state },
    });
    return response.data;
  },

  async createDriveIntegration(
    idToken: string,
    spaceId: string,
    data: {
      connector_id: string;
      resource_id: string;
      resource_name: string;
      resource_path?: string;
      sync_mode?: 'auto' | 'webhook';
      sync_interval_minutes?: number;
      sync_enabled?: boolean;
    }
  ): Promise<{ success: boolean; data: any }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.post(`/kms/spaces/${spaceId}/storage/integrations`, data);
    return response.data;
  },

  async listDriveIntegrations(
    idToken: string,
    spaceId: string
  ): Promise<{ data: { items: any[]; total: number } }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.get(`/kms/spaces/${spaceId}/storage/integrations`);
    return response.data;
  },

  async getDriveIntegration(
    idToken: string,
    spaceId: string,
    integrationId: string
  ): Promise<{ data: any }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.get(
      `/kms/spaces/${spaceId}/storage/integrations/${integrationId}`
    );
    return response.data;
  },

  async updateDriveIntegration(
    idToken: string,
    spaceId: string,
    integrationId: string,
    updates: {
      drive_folder_name?: string;
      sync_mode?: 'auto' | 'webhook';
      sync_interval_minutes?: number;
      sync_enabled?: boolean;
    }
  ): Promise<{ success: boolean; data: any }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.put(
      `/kms/spaces/${spaceId}/storage/integrations/${integrationId}`,
      updates
    );
    return response.data;
  },

  async deleteDriveIntegration(
    idToken: string,
    spaceId: string,
    integrationId: string,
    deleteDocuments: boolean = false
  ): Promise<{ success: boolean }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.delete(
      `/kms/spaces/${spaceId}/storage/integrations/${integrationId}`,
      {
        params: { delete_documents: deleteDocuments },
      }
    );
    return response.data;
  },

  async triggerDriveSync(
    idToken: string,
    spaceId: string,
    integrationId: string,
    deleteExisting: boolean = false
  ): Promise<{ success: boolean; message: string; next_sync_at?: string }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.post(
      `/kms/spaces/${spaceId}/storage/integrations/${integrationId}/sync`,
      {},
      {
        params: {
          delete_existing: deleteExisting, // Always send the parameter
        },
      }
    );
    return response.data;
  },

  async listDriveSyncedFiles(
    idToken: string,
    spaceId: string,
    integrationId: string
  ): Promise<{ data: any[] }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.get(
      `/kms/spaces/${spaceId}/storage/integrations/${integrationId}/files`
    );
    return response.data;
  },

  async syncDriveFileToProcess(
    idToken: string,
    spaceId: string,
    integrationId: string,
    driveFileId: string
  ): Promise<{ success: boolean; message: string; file_id: string; file_name: string }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.post(
      `/kms/spaces/${spaceId}/storage/integrations/${integrationId}/sync-file`,
      null,
      {
        params: { drive_file_id: driveFileId },
      }
    );
    return response.data;
  },

  // ========================================
  // Widget Methods
  // ========================================

  /**
   * Create a new widget
   */
  async createWidget(idToken: string, data: WidgetFormData): Promise<{ data: Widget }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.post('/kms/widgets', data);
    return response.data;
  },

  /**
   * List all widgets with pagination
   */
  async listWidgets(
    idToken: string,
    params?: { skip?: number; limit?: number }
  ): Promise<{ data: WidgetListResponse }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.get('/kms/widgets', { params });
    return response.data;
  },

  /**
   * Get a specific widget
   */
  async getWidget(idToken: string, widgetId: string): Promise<{ data: Widget }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.get(`/kms/widgets/${widgetId}`);
    return response.data;
  },

  /**
   * Update a widget
   */
  async updateWidget(
    idToken: string,
    widgetId: string,
    data: Partial<WidgetFormData>
  ): Promise<{ data: Widget }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.put(`/kms/widgets/${widgetId}`, data);
    return response.data;
  },

  /**
   * Delete a widget
   */
  async deleteWidget(idToken: string, widgetId: string): Promise<void> {
    const client = createApiClient(idToken, 'kms');
    await client.delete(`/kms/widgets/${widgetId}`);
  },

  /**
   * Batch update widget associations for a space
   */
  async updateSpaceWidgets(
    idToken: string,
    spaceId: string,
    widgetIds: string[]
  ): Promise<{ data: { widget_count: number } }> {
    const client = createApiClient(idToken, 'kms');
    return client.put(`/kms/spaces/${spaceId}/widgets`, {
      widget_ids: widgetIds,
    });
  },

  /**
   * Get embed code for widget + space combination
   */
  async getWidgetEmbedCode(
    idToken: string,
    widgetId: string,
    spaceId: string
  ): Promise<{ data: EmbedCodeResponse }> {
    const client = createApiClient(idToken, 'kms');
    const response = await client.get(`/kms/widgets/${widgetId}/spaces/${spaceId}/embed-code`);
    return response.data;
  },
};
